import { useCallback, useEffect } from 'react';
import { collection, getDocs, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';

/**
 * Runs (at most weekly, tracked in localStorage) the check that gives every
 * active employee an "Özgeçmiş Güncelleme" task when they have none open and
 * their last completed one is older than three months. Triggered by opening
 * the dashboard.
 */
const useCVUpdateTaskCheck = () => {
  const checkAndCreateCVUpdateTasks = useCallback(async () => {
    try {
      // Son kontrol zamanını localStorage'dan al
      const lastCheckKey = 'cvTasksLastCheck';
      const lastCheck = localStorage.getItem(lastCheckKey);
      const now = new Date();
      
      // Eğer son kontrol 7 gün içinde yapıldıysa, tekrar kontrol etme
      if (lastCheck) {
        const lastCheckDate = new Date(lastCheck);
        const daysSinceLastCheck = (now - lastCheckDate) / (1000 * 60 * 60 * 24);
        if (daysSinceLastCheck < 7) {
          console.log(`Özgeçmiş görevleri ${daysSinceLastCheck.toFixed(1)} gün önce kontrol edildi, tekrar kontrol gerekmiyor`);
          return;
        }
      }

      console.log('Özgeçmiş güncelleme görevleri kontrol ediliyor...');
      
      const today = new Date();
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

      // Tüm çalışanları getir
      const employeesRef = collection(db, 'Employees');
      const employeesSnapshot = await getDocs(employeesRef);
      const allEmployees = employeesSnapshot.docs
        .filter(doc => {
          const data = doc.data();
          // Sadece aktif çalışanlar için görev oluştur
          return data.Active === 1 || data.Active === '1' || data.state === 'aktif';
        })
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

      console.log(`${allEmployees.length} aktif çalışan bulundu`);

      let createdCount = 0;

      for (const employee of allEmployees) {
        const employeeName = `${employee.eName || ''} ${employee.eSurname || ''}`.trim();
        
        // Bu çalışan için özgeçmiş güncelleme görevi var mı kontrol et
        const tasksRef = collection(db, 'Tasks');
        const tasksSnapshot = await getDocs(tasksRef);
        
        // Bu çalışana ait özgeçmiş güncelleme görevlerini bul
        const existingCVTasks = tasksSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(task => 
            task.isCVUpdateTask === true &&
            task.assigneeId === employee.id &&
            task.status !== 'completed' &&
            task.status !== 'cancelled'
          );

        // Eğer aktif bir özgeçmiş güncelleme görevi yoksa, yeni oluştur
        if (existingCVTasks.length === 0) {
          // Son tamamlanan özgeçmiş güncelleme görevini kontrol et
          const completedCVTasks = tasksSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(task => 
              task.isCVUpdateTask === true &&
              task.assigneeId === employee.id &&
              task.status === 'completed'
            )
            .sort((a, b) => {
              const aDate = a.completedAt?.toDate?.() || new Date(0);
              const bDate = b.completedAt?.toDate?.() || new Date(0);
              return bDate - aDate;
            });

          let shouldCreateTask = false;

          if (completedCVTasks.length === 0) {
            // Hiç özgeçmiş güncelleme görevi tamamlanmamış, yeni görev oluştur
            shouldCreateTask = true;
          } else {
            // Son tamamlanan görev 3 aydan eski mi kontrol et
            const lastCompletedTask = completedCVTasks[0];
            const lastCompletedDate = lastCompletedTask.completedAt?.toDate?.() || new Date(0);
            
            if (lastCompletedDate < threeMonthsAgo) {
              shouldCreateTask = true;
            }
          }

          if (shouldCreateTask) {
            // Yeni özgeçmiş güncelleme görevi oluştur
            const dueDate = new Date(today);
            dueDate.setDate(dueDate.getDate() + 7); // 1 hafta içinde tamamlanmalı

            const newTask = {
              title: 'Özgeçmiş Güncelleme',
              description: `Lütfen özgeçmişinizi güncelleyiniz. Özgeçmişinizde son 3 aydaki değişiklikleri (yeni projeler, eğitimler, sertifikalar vb.) eklemeyi unutmayınız.`,
              priority: 'medium',
              status: 'pending',
              assigneeId: employee.id,
              assigneeName: employeeName,
              assignees: [{
                id: employee.id,
                name: employeeName,
                email: employee.eMail || ''
              }],
              dueDate: Timestamp.fromDate(dueDate),
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              isCVUpdateTask: true, // Özgeçmiş güncelleme görevi olduğunu işaretle
              autoCreated: true, // Otomatik oluşturulduğunu işaretle
              createdBy: 'system' // Sistem tarafından oluşturuldu
            };

            await addDoc(collection(db, 'Tasks'), newTask);
            createdCount++;
            console.log(`✅ ${employeeName} için özgeçmiş güncelleme görevi oluşturuldu`);
          }
        }
      }

      // Son kontrol zamanını kaydet
      localStorage.setItem(lastCheckKey, now.toISOString());
      
      if (createdCount > 0) {
        console.log(`✅ ${createdCount} yeni özgeçmiş güncelleme görevi oluşturuldu`);
      } else {
        console.log('✓ Tüm çalışanların özgeçmiş güncelleme görevleri güncel');
      }
    } catch (error) {
      console.error('Özgeçmiş güncelleme görevleri oluşturulurken hata:', error);
    }
  }, []);

  useEffect(() => {
    // Dashboard yüklendiğinde özgeçmiş güncelleme görevlerini kontrol et
    checkAndCreateCVUpdateTasks();
  }, [checkAndCreateCVUpdateTasks]);
};

export default useCVUpdateTaskCheck;
